import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import { ErrorBox, Loading } from "../components/Loading";
import type {
  UpcomingMeleeEvent,
  UpcomingMeleeEventsResponse,
} from "@liga/shared";

const ONE_HOUR_MS = 60 * 60 * 1000;
const FALLBACK_BRAND_IMAGE = "https://cdn.melee.gg/images/blankbrandimage.png";

/**
 * Display the start time in São Paulo time — the league is local to Campinas
 * and most visitors are in BRT. Falls back to the raw string on parse errors.
 */
function formatStart(startDate: string): {
  dateLabel: string;
  timeLabel: string;
} {
  const dt = new Date(startDate);
  if (Number.isNaN(dt.getTime())) {
    return { dateLabel: startDate, timeLabel: "" };
  }
  const dateLabel = new Intl.DateTimeFormat("pt-BR", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    timeZone: "America/Sao_Paulo",
  }).format(dt);
  const timeLabel = new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "America/Sao_Paulo",
  }).format(dt);
  return {
    dateLabel: dateLabel.replace(/\.$/, ""),
    timeLabel,
  };
}

function formatRefreshedAt(ms: number): string {
  const dt = new Date(ms);
  if (Number.isNaN(dt.getTime())) return "";
  return new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    day: "2-digit",
    month: "2-digit",
    timeZone: "America/Sao_Paulo",
  }).format(dt);
}

function EventCard({ event }: { event: UpcomingMeleeEvent }) {
  const { dateLabel, timeLabel } = formatStart(event.startDate);
  const href = `https://melee.gg/Tournament/View/${event.meleeEventId}`;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex flex-col overflow-hidden rounded-2xl border border-line bg-white transition-all hover:-translate-y-0.5 hover:shadow-lg hover:border-brand-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
    >
      <div className="relative aspect-[16/9] overflow-hidden bg-[#ece7dc]">
        <img
          src={event.brandImageUrl || FALLBACK_BRAND_IMAGE}
          alt=""
          loading="lazy"
          onError={(e) => {
            const img = e.currentTarget;
            if (img.src !== FALLBACK_BRAND_IMAGE) img.src = FALLBACK_BRAND_IMAGE;
          }}
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
        />
        {event.format && (
          <span className="absolute top-3 left-3 rounded-full bg-brand-700 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white shadow">
            {event.format}
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-3 p-5">
        <div className="flex items-baseline gap-2 text-[12px] font-semibold uppercase tracking-[0.14em] text-[#8a8170]">
          <span>{dateLabel}</span>
          {timeLabel && (
            <>
              <span className="text-[#cbc3b2]">·</span>
              <span>{timeLabel}</span>
            </>
          )}
        </div>
        <h3 className="font-display text-[19px] leading-snug font-bold text-ink group-hover:text-brand-700">
          {event.name}
        </h3>
        <div className="mt-auto flex items-center justify-between text-[13px] text-[#6b6356]">
          <span>
            <span className="font-semibold text-ink">{event.playerCount}</span>
            {event.playerCap > 0 && (
              <span className="text-[#a39a87]"> / {event.playerCap}</span>
            )}{" "}
            inscritos
          </span>
          <span className="font-semibold text-brand-700 group-hover:underline">
            Ver no Melee →
          </span>
        </div>
      </div>
    </a>
  );
}

export function HomePage() {
  // Backend caches the melee.gg snapshot for an hour. Front-end uses a matching
  // stale time so re-mounts within the hour don't even hit the API, but a
  // background tab left open longer will re-validate when focused.
  const query = useQuery<UpcomingMeleeEventsResponse>({
    queryKey: ["meleeUpcomingEvents"],
    queryFn: () => api.meleeUpcomingEvents(),
    staleTime: ONE_HOUR_MS,
    gcTime: ONE_HOUR_MS * 2,
    refetchOnWindowFocus: true,
  });

  return (
    <div className="space-y-8">
      <div className="space-y-3">
        <div className="text-xs uppercase tracking-[0.18em] text-[#8a8170] font-bold">
          Próximos torneios
        </div>
        <h1 className="font-display text-[44px] leading-none font-bold tracking-tight text-ink">
          Eventos Legacy da Mont
        </h1>
        <p className="max-w-[640px] text-[15px] leading-relaxed text-[#6b6356]">
          Os próximos torneios de Legacy organizados pela Mont CardShop no
          melee.gg. Clique em um card para abrir a página do evento e se
          inscrever.
        </p>
      </div>

      {query.isLoading && <Loading label="Buscando eventos no Melee..." />}
      {query.error && !query.data && <ErrorBox error={query.error} />}

      {query.data && query.data.events.length === 0 && (
        <div className="rounded-2xl border border-dashed border-line bg-white/60 px-6 py-12 text-center text-[#6b6356]">
          Nenhum torneio de Legacy agendado no momento. Volte mais tarde!
        </div>
      )}

      {query.data && query.data.events.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 items-stretch">
          {query.data.events.map((event) => (
            <EventCard key={event.meleeEventId} event={event} />
          ))}
        </div>
      )}

      {query.data && (
        <div className="text-[12px] text-[#a39a87]">
          Atualizado em {formatRefreshedAt(query.data.fetchedAt)} ·{" "}
          <a
            href={`https://melee.gg/Hub/Organization/${query.data.organizationId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-brand-700"
          >
            Ver organização no Melee
          </a>
        </div>
      )}
    </div>
  );
}
